import React from 'react';
import { forwardRef } from 'react';

import { tv } from 'tailwind-variants';

import Loading from '../../components/Loading';

import cn from '../../utils/cn';

export type Size = 'regular' | 'condensed';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  condensed?: boolean;
  loading?: boolean;
  error?: boolean;
  size?: Size;
}

const customIcon = (icon: React.ReactNode, position: 'start' | 'end') => {
  return React.cloneElement(icon as React.ReactElement, {
    className: cn('absolute top-0.5 bottom-0 m-auto text-gray size-4', position === 'start' ? 'left-3' : 'right-1.5'),
  });
};

const inputVariants = tv({
  base: `
    border-input flex h-9 w-full rounded-md border border-gray-light bg-white px-3 py-1 text-sm shadow-sm
    placeholder:text-gray-light focus-visible:outline-none focus-visible:border-black
    file:border-0 file:bg-transparent file:text-sm file:font-medium
    disabled:cursor-not-allowed disabled:bg-gray-lightest disabled:border-gray disabled:text-gray
    [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
  `,
  variants: {
    size: {
      regular: 'h-9',
      condensed: 'h-8',
    },
    error: {
      true: 'border-danger',
      false: '',
    },
    startIcon: {
      true: 'pl-8',
      false: '',
    },
    endIcon: {
      true: 'pr-8',
      false: '',
    },
    loading: {
      true: 'pr-8',
      false: '',
    },
  },
});

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, startIcon = false, endIcon = false, loading = false, error = false, size = 'regular', ...props }, ref) => {
    return (
      <div className='relative'>
        {startIcon ? customIcon(startIcon, 'start') : null}
        <input
          type={type}
          className={cn(
            inputVariants({
              size,
              loading,
              error,
              startIcon: !!startIcon,
              endIcon: !!endIcon,
            }),
            className
          )}
          ref={ref}
          {...props}
        />
        {loading ? <Loading className='absolute top-0 bottom-0 m-auto size-4 right-3' /> : null}
        {endIcon ? customIcon(endIcon, 'end') : null}
      </div>
    );
  }
);
Input.displayName = 'Input';

export default Input;
